import { isEqual } from "lodash";
import {
  ArrowLeft,
  ArrowRight,
  RotateCw,
  Scissors,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import {
  PDFViewer,
  type PDFViewerRef,
} from "@/components/shared/file-viewers/pdf-viewer";

import { ToolbarButton } from "@/components/shared/toolbars/toolbar-button";
import { Shortcut } from "@/components/ui/shortcut";
import { Skeleton } from "@/components/ui/skeleton";
import { TypographySmall } from "@/components/ui/typography";

import type { EditablePage } from "../types";
import { getCachedBlobUrl } from "../utils/pdf-blob-cache";

type _PagesViewerProps = {
  url: string | undefined;
  pageIndex: number;
  currentPage: number;
  totalPages: number;
  disableEditControls: boolean;
  onNavigate: (pageIndex: number) => void;
  onSplit: (pageIndex: number) => void;
};

const _PagesViewer = forwardRef<PDFViewerRef, _PagesViewerProps>(
  function _PagesViewer(
    {
      url,
      pageIndex,
      currentPage,
      totalPages,
      disableEditControls = false,
      onNavigate,
      onSplit,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const pdfViewerRef = useRef<PDFViewerRef>(null);

    useImperativeHandle(ref, () => pdfViewerRef.current!);

    const [containerDimensions, setContainerDimensions] = useState({
      width: 0,
      height: 0,
    });

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const updateDimensions = () => {
        if (containerRef.current) {
          const width = containerRef.current.clientWidth;
          const height = containerRef.current.clientHeight;
          setContainerDimensions({ width, height });
        }
      };

      updateDimensions();
      window.addEventListener("resize", updateDimensions);

      return () => window.removeEventListener("resize", updateDimensions);
    }, []);

    const handleRotation = () => {
      pdfViewerRef.current?.rotate();
    };

    const handleZoomIn = () => {
      pdfViewerRef.current?.zoomIn();
    };

    const handleZoomOut = () => {
      pdfViewerRef.current?.zoomOut();
    };

    const handleSplit = (cut: "left" | "right" = "right") => {
      let cutDirection = 0;

      if (
        (currentPage === 0 && cut === "left") ||
        (currentPage === totalPages - 1 && cut === "right")
      ) {
        return;
      }

      cutDirection = cut === "left" ? -1 : 0;
      onSplit(currentPage + cutDirection);
    };

    useHotkeys(
      ["s"],
      () => {
        handleSplit("right");
      },
      { preventDefault: true },
    );

    useHotkeys(
      ["a"],
      () => {
        handleSplit("left");
      },
      { preventDefault: true },
    );

    const handleNavigate = (direction: "next" | "previous") => {
      let newPage: number;

      if (direction === "next") {
        newPage = Math.min(currentPage + 1, totalPages - 1);
      } else {
        newPage = Math.max(currentPage - 1, 0);
      }

      onNavigate(newPage);
    };

    useHotkeys(["numpadadd", "+", "arrowright"], () => {
      handleNavigate("next");
    });

    useHotkeys(["numpadsubtract", "-", "arrowleft"], () => {
      handleNavigate("previous");
    });

    if (!url) return <Skeleton className="h-full w-full" />;

    const resolvedUrl = (url && (getCachedBlobUrl(url) ?? url)) ?? "";

    return (
      <div
        className="h-full flex flex-col gap-2 py-1"
        style={{
          ...(containerDimensions.width && {
            width: containerDimensions.width,
          }),
        }}
      >
        <div
          ref={containerRef}
          className="flex-1 relative overflow-auto bg-gray-200"
        >
          <PDFViewer
            renderOnlyOnePage
            ref={pdfViewerRef}
            pdfBlob={resolvedUrl}
            currentPage={pageIndex}
            coordinates={undefined}
            className="bg-gray-100"
          />
        </div>
        <div className="flex flex-row justify-center items-center">
          <div className="flex flex-row border rounded-sm bg-background">
            <ToolbarButton
              onClick={() => handleNavigate("previous")}
              disabled={currentPage === 0}
              tooltip={t("back")}
            >
              <ArrowLeft width={16} />
            </ToolbarButton>
            <div className="flex flex-row gap-2 items-center rounded-none px-2">
              <TypographySmall>{currentPage + 1}</TypographySmall>
              <span className="text-muted-foreground"> / </span>
              <TypographySmall>{totalPages}</TypographySmall>
            </div>
            <ToolbarButton
              onClick={() => handleNavigate("next")}
              disabled={currentPage === totalPages - 1}
              tooltip={t("next")}
              className="border border-t-0 border-l-0 border-b-0 rounded-none"
            >
              <ArrowRight width={16} />
            </ToolbarButton>
            <ToolbarButton
              onClick={handleSplit}
              disabled={disableEditControls}
              tooltip={
                <div className="flex flex-row gap-2 items-center">
                  <TypographySmall>{t("split_pdf_toolbar")}</TypographySmall>
                  <Shortcut.ShortcutRoot>
                    <Shortcut.ShortcutHotKey
                      className="bg-muted-foreground"
                      size="sm"
                    >
                      A
                    </Shortcut.ShortcutHotKey>
                  </Shortcut.ShortcutRoot>
                  <TypographySmall>{t("and")}</TypographySmall>
                  <Shortcut.ShortcutRoot>
                    <Shortcut.ShortcutHotKey
                      className="bg-muted-foreground"
                      size="sm"
                    >
                      S
                    </Shortcut.ShortcutHotKey>
                  </Shortcut.ShortcutRoot>
                </div>
              }
              className="border border-t-0 border-l-0 border-b-0 rounded-none"
            >
              <Scissors width={16} />
            </ToolbarButton>
            <ToolbarButton onClick={handleRotation} tooltip={t("rotate")}>
              <RotateCw width={16} />
            </ToolbarButton>
            <ToolbarButton
              onClick={handleZoomIn}
              tooltip={t("zoom_in")}
              className="border border-t-0 border-b-0 rounded-none"
            >
              <ZoomIn width={16} />
            </ToolbarButton>
            <ToolbarButton onClick={handleZoomOut} tooltip={t("zoom_out")}>
              <ZoomOut width={16} />
            </ToolbarButton>
          </div>
        </div>
      </div>
    );
  },
);

const pdfCache = new Map<string, string>();

const RawPagesViewer = ({
  pages: _pages,
  children,
  onSplit,
}: {
  pages: EditablePage[];
  onSplit: (pageIndex: number) => void;
  children: (props: {
    currentPage: number;
    setCurrentPage: (pageIndex: number) => void;
    setPages: (pages: EditablePage[]) => void;
    currentInvoiceId: string | null;
    setCurrentInvoiceId: (invoiceId: string | null) => void;
  }) => React.ReactNode;
}) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | null>(null);
  const pagesViewerRef = useRef<PDFViewerRef>(null);
  const [pages, setPages] = useState<EditablePage[]>(_pages);

  useEffect(() => {
    return () => {
      if (pdfCache.size > 10) {
        const entries = Array.from(pdfCache.entries());
        pdfCache.clear();
        entries.slice(-5).forEach(([key, value]) => {
          pdfCache.set(key, value);
        });
      }
    };
  }, []);

  useEffect(() => {
    setPages(_pages);
  }, [_pages]);

  const handleNavigate = (pageIndex: number) => {
    setCurrentPage(pageIndex);
  };

  const handleSetCurrentPage = (pageIndex: number) => {
    setCurrentPage(pageIndex);
  };

  const handleSetPages = (_pages: EditablePage[]) => {
    if (isEqual(pages, _pages)) {
      return;
    }
    setPages(_pages);
  };

  const pageIndex = pages[currentPage]?.sourcePageIndex ?? 0;

  return (
    <div className="h-full flex flex-row gap-2">
      <div
        className="pl-2"
        style={{
          width: `${100 / (Math.SQRT2 / 0.9)}vh`,
        }}
      >
        <_PagesViewer
          totalPages={pages.length}
          ref={pagesViewerRef}
          url={pages[currentPage]?.sourcePdfUrl}
          pageIndex={pageIndex}
          currentPage={currentPage}
          disableEditControls={Boolean(pages[currentPage]?.sourcePdfUrl)}
          onNavigate={handleNavigate}
          onSplit={onSplit}
        />
      </div>
      {children({
        currentPage,
        setCurrentPage: handleSetCurrentPage,
        setPages: handleSetPages,
        currentInvoiceId,
        setCurrentInvoiceId,
      })}
    </div>
  );
};

export const PagesViewer = memo(RawPagesViewer);
